import { useEffect } from 'react';
import { usePanelStore } from '@/panels/panelStore';
import { useAppStore } from './appStore';
import { findShortcut, isEditableTarget } from './shortcuts';

/** Selector + setter contract used by the workbench Shell. */
export type ShortcutHandlers = {
  /** Open the global command palette. */
  openCommandPalette: () => void;
  /** Move focus to the chat composer textarea. */
  focusChatComposer: () => void;
  /** Toggle the LeftNav (also expands when chat collapsed). */
  toggleLeftNav: () => void;
  /** Toggle the chat pane visibility. */
  toggleChat: () => void;
  /** Close the focused panel (no-op when none focused). */
  closeFocusedPanel: () => void;
  /** Toggle maximize on the focused panel (handled inside PanelShell). */
  maximizeFocusedPanel: () => void;
  /** Open the keyboard shortcut reference dialog. */
  openShortcutHelp: () => void;
};

/**
 * Subscribes to `keydown` once for the lifetime of the Shell and dispatches
 * registered shortcuts to the matching handler.
 *
 * Behavior:
 *  - Most shortcuts are blocked when typing in inputs/textareas/contenteditable
 *    so the browser-native editing keys still work. The `?` help key and any
 *    shortcut marked `allowInInputs` opt out of that gate.
 *  - Modifier-bearing shortcuts (`mod+…`) always fire, even from within an
 *    input — `⌘K` is expected to work while the user is typing in the chat.
 *  - The hook never preventDefault when no shortcut matches, so the browser
 *    keeps its native behavior.
 */
export function useGlobalShortcuts(handlers: ShortcutHandlers): void {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const def = findShortcut(event);
      if (!def) return;

      const editable = isEditableTarget(event.target);
      const hasModifier = event.metaKey || event.ctrlKey;

      // Bare-key shortcuts inside editable elements would steal typing.
      if (editable && !hasModifier && !def.allowInInputs) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      switch (def.id) {
        case 'commandPalette':
          handlers.openCommandPalette();
          break;
        case 'focusChat':
          handlers.focusChatComposer();
          break;
        case 'toggleLeftNav':
          handlers.toggleLeftNav();
          break;
        case 'toggleChat':
          handlers.toggleChat();
          break;
        case 'closeFocusedPanel':
          handlers.closeFocusedPanel();
          break;
        case 'maximizeFocusedPanel':
          handlers.maximizeFocusedPanel();
          break;
        case 'openShortcutHelp':
          handlers.openShortcutHelp();
          break;
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlers]);
}

/** Hook variant that wires shortcuts into the existing app + panel stores. */
export function useShellShortcuts(extra: {
  openShortcutHelp: () => void;
  focusChatComposer: () => void;
  maximizeFocusedPanel: () => void;
}): void {
  const setCommandPaletteOpen = useAppStore((s) => s.setCommandPaletteOpen);
  const toggleLeftNav = useAppStore((s) => s.toggleLeftNav);
  const toggleChat = useAppStore((s) => s.toggleChat);
  const closePanel = usePanelStore((s) => s.closePanel);
  const focusedPanelId = usePanelStore((s) => s.focusedPanelId);
  const panels = usePanelStore((s) => s.panels);

  useGlobalShortcuts({
    openCommandPalette: () => setCommandPaletteOpen(true),
    focusChatComposer: extra.focusChatComposer,
    toggleLeftNav,
    toggleChat,
    closeFocusedPanel: () => {
      // Prefer the explicitly focused panel; otherwise fall back to the most
      // recently opened non-detached panel so ⌘. always closes "the latest".
      if (focusedPanelId) {
        closePanel(focusedPanelId);
        return;
      }
      const fallback = [...panels].reverse().find((p) => !p.detached);
      if (fallback) closePanel(fallback.panelId);
    },
    maximizeFocusedPanel: extra.maximizeFocusedPanel,
    openShortcutHelp: extra.openShortcutHelp,
  });
}
